/**
 * InsightVariablesStep — create-wizard step 3 (W12 Part B, Task B3).
 *
 * The ACCEPTED variables of the insight-variable generation chosen on step 1
 * (the generation Select itself lives there — AnalysisStep, Task B2; this step
 * never offers a second one). The empty state keeps the AC-021 escape:
 * «Review variables in Insights».
 */
import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { STRATEGY_LIST } from '@test-kit/selectors/strategy-list';
import { SelectionTable, type SelectionRow } from '../SelectionTable';

export interface InsightVariablesStepProps {
  rows: SelectionRow[];
  selectedIds: string[];
  onSelectedIdsChange: (ids: string[]) => void;
  loading?: boolean;
  error?: string | null;
  /** «N of M accepted selected · from analysis X, generation #K» (dialog-computed). */
  summary: string;
  /**
   * Why the variable package blocks «Create with AI» (stale generation, not
   * completed, no accepted variables, empty package). One caption line with the
   * ESCAPE next to it — never an Alert: the blocker PRESENTATION belongs to the
   * readiness rows on the review step.
   */
  blockingReason?: string | null;
  /** No generation chosen — the strategy runs on product variables only. */
  generationChosen: boolean;
  /** Rendered in the empty state when the caller can navigate to Insights. */
  onReviewVariables?: () => void;
}

export const InsightVariablesStep: React.FC<InsightVariablesStepProps> = ({
  rows,
  selectedIds,
  onSelectedIdsChange,
  loading = false,
  error = null,
  summary,
  blockingReason = null,
  generationChosen,
  onReviewVariables,
}) => (
  <Box data-testid={STRATEGY_LIST.sourceInsightVariables}>
    {blockingReason && (
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        data-testid={STRATEGY_LIST.variablePackageBlockingReason}
        sx={{ mb: 1.5 }}
      >
        <Typography variant="body2" color="error.main" sx={{ flex: 1, minWidth: 0 }}>
          {blockingReason}
        </Typography>
        {onReviewVariables && (
          <Button
            size="small"
            variant="outlined"
            onClick={onReviewVariables}
            data-testid={STRATEGY_LIST.sourceInsightVariablesReview}
          >
            Review variables in Insights
          </Button>
        )}
      </Stack>
    )}

    <SelectionTable
      rows={rows}
      selectedIds={selectedIds}
      onSelectedIdsChange={onSelectedIdsChange}
      loading={loading}
      error={error}
      searchPlaceholder="Search insight variables"
      summary={summary}
      emptyState={(
        <Stack spacing={1.5} alignItems="center" sx={{ py: 3 }}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {generationChosen
              ? 'No accepted variables in this generation — review them in Insights.'
              : 'This analysis has no accepted insight-derived variables. The strategy will use the project variables only.'}
          </Typography>
          {/* One escape at a time: while the blocking caption above owns it,
              the empty state must not render a second identical action. */}
          {onReviewVariables && !blockingReason && (
            <Button
              size="small"
              variant="outlined"
              onClick={onReviewVariables}
              data-testid={STRATEGY_LIST.sourceInsightVariablesReview}
            >
              Review variables in Insights
            </Button>
          )}
        </Stack>
      )}
      aria-label="Insight-derived variables"
      data-testid={STRATEGY_LIST.wizardTable('insightVars')}
    />
  </Box>
);

InsightVariablesStep.displayName = 'InsightVariablesStep';
